<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  src: { type: String, required: true },
  width: { type: [Number, String], default: null },
  height: { type: [Number, String], default: null },
  maxWidth: { type: Number, default: 240 },
  maxHeight: { type: Number, default: 180 },
  fixedFrame: { type: Boolean, default: false },
});
const emit = defineEmits(['open']);
const ready = ref(false);
const failed = ref(false);
const attempt = ref(0);

// Freeze the initial frame for this source so late image metadata cannot move messages.
const width = Number(props.width);
const height = Number(props.height);
const knownSize = Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0;
const scale = knownSize ? Math.min(1, props.maxWidth / width, props.maxHeight / height) : 1;
const frameWidth = !props.fixedFrame && knownSize ? width * scale : props.maxWidth;
const frameHeight = !props.fixedFrame && knownSize ? height * scale : props.maxHeight;
const frameStyle = { width: frameWidth + 'px', aspectRatio: frameWidth + ' / ' + frameHeight };
const label = computed(() => failed.value ? '图片加载失败，点击重试' : ready.value ? '查看完整图片' : '图片正在加载');

const activate = () => {
  if (failed.value) {
    ready.value = false;
    failed.value = false;
    attempt.value += 1;
  } else if (ready.value) {
    emit('open');
  }
};
const onError = () => {
  ready.value = false;
  failed.value = true;
};
</script>

<template>
  <div
    class="relative group shrink-0 max-w-full overflow-hidden rounded-lg"
    :class="failed ? 'cursor-pointer' : ready ? 'cursor-zoom-in' : 'cursor-wait'"
    :style="frameStyle"
    role="button"
    tabindex="0"
    :aria-label="label"
    :aria-busy="!ready && !failed"
    @click.stop="activate"
    @keydown.enter.self.prevent="activate"
    @keydown.space.self.prevent="activate"
  >
    <div
      v-if="!failed"
      aria-hidden="true"
      class="absolute inset-0 pointer-events-none bg-gradient-to-br from-white via-slate-100 to-white dark:from-slate-800 dark:via-slate-700 dark:to-slate-800 transition-opacity duration-[250ms] motion-reduce:transition-none"
      :class="ready ? 'opacity-0' : 'opacity-100 motion-safe:animate-pulse'"
    />
    <div v-if="failed" class="absolute inset-0 flex flex-col items-center justify-center gap-1 p-1 bg-n-alpha-1 text-n-slate-11 text-xs text-center">
      <span class="i-lucide-image-off size-4 shrink-0" aria-hidden="true" />
      <span>点击重试</span>
    </div>
    <div v-else class="absolute inset-0 flex items-center justify-center">
      <img
        :key="attempt"
        :src="src"
        alt="图片附件"
        class="skip-context-menu block w-auto h-auto max-w-full max-h-full object-contain transition-opacity duration-[250ms] motion-reduce:transition-none"
        :class="ready ? 'opacity-100' : 'opacity-0'"
        @load="ready = true"
        @error="onError"
      />
    </div>
    <slot v-if="ready" />
  </div>
</template>
